import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

interface ReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  serverId?: string;
  commentId?: string;
  serverName?: string;
}

export const ReportDialog: React.FC<ReportDialogProps> = ({
  isOpen,
  onClose,
  serverId,
  commentId,
  serverName
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const reportReasons = [
    { id: 'inappropriate_content', label: t('report.reasons.inappropriate_content') },
    { id: 'spam', label: t('report.reasons.spam') },
    { id: 'fake_server', label: t('report.reasons.fake_server') },
    { id: 'malicious_content', label: t('report.reasons.malicious_content') },
    { id: 'copyright', label: t('report.reasons.copyright') },
    { id: 'other', label: t('report.reasons.other') }
  ];

  const reportMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error(t('report.error'));
      
      const reason = selectedReason === 'other' ? customReason : reportReasons.find(r => r.id === selectedReason)?.label || '';
      
      const { error } = await supabase
        .from('reports')
        .insert({
          server_id: serverId || null,
          comment_id: commentId || null,
          reporter_id: user.id,
          reason: reason,
          status: 'pending'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('report.success'));
      onClose();
      setSelectedReason('');
      setCustomReason('');
    },
    onError: (error) => {
      console.error('Erreur lors du signalement:', error);
      toast.error(t('report.error'));
    }
  });

  const handleSubmit = () => {
    if (!selectedReason) {
      toast.error(t('report.selectReason'));
      return;
    }
    
    if (selectedReason === 'other' && !customReason.trim()) {
      toast.error(t('report.specifyReason'));
      return;
    }

    reportMutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent 
        className="bg-slate-800 border-slate-700 text-white p-4 sm:p-6 sm:max-w-lg"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          onClose();
        }}
        onEscapeKeyDown={onClose}
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {t('report.title', { 
              type: commentId ? t('report.titleComment') : t('report.titleServer') 
            })}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {serverName && t('report.serverName', { serverName })}
            <br />
            {t('report.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-white mb-3 block">{t('report.reasonLabel')}</Label>
            <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
              {reportReasons.map((reason) => (
                <div key={reason.id} className="flex items-center space-x-3 min-h-[44px]">
                  <RadioGroupItem value={reason.id} id={reason.id} className="w-5 h-5" />
                  <Label htmlFor={reason.id} className="text-slate-300 cursor-pointer text-sm sm:text-base">
                    {reason.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {selectedReason === 'other' && (
            <div>
              <Label htmlFor="custom-reason" className="text-white mb-2 block">
                {t('report.customReasonLabel')}
              </Label>
              <Textarea
                id="custom-reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={t('report.customReasonPlaceholder')}
                className="bg-slate-700 border-slate-600 text-white"
                maxLength={500}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={reportMutation.isPending} className="w-full sm:w-auto min-h-[44px]">
            {t('report.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={reportMutation.isPending || !selectedReason}
            className="bg-red-600 hover:bg-red-700 w-full sm:w-auto min-h-[44px]"
          >
            {reportMutation.isPending ? t('report.submitting') : t('report.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};